/**
 * Wann ein Beleg nicht mehr bearbeitet werden darf.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Die Grenze ist der Versand – und sonst nichts
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Bis zum Hotfix war die Grenze `status === 'entwurf'`. Das klang vernünftig
 * und war in der Praxis falsch: Sobald ein Beleg eine Nummer bekam, war er
 * gesperrt – auch wenn er noch auf keinem Bildschirm ausser dem eigenen
 * gewesen war. Wer sich in der Menge vertippt hatte, konnte nichts mehr tun,
 * ausser zu stornieren und von vorne zu beginnen. Dabei verbrennt jedes Mal
 * eine Nummer, und der Kunde bekommt später eine Rechnung mit einer Lücke
 * davor.
 *
 * Massgeblich ist jetzt eine einzige Frage: **Ist dieser Beleg draussen?**
 *
 * Draussen heisst eines von beidem:
 *
 *   · Eine E-Mail mit dem Beleg ist nachweislich hinausgegangen – der
 *     SMTP-Versand war erfolgreich und wurde erfasst.
 *
 *   · Jemand hat ihn ausdrücklich von Hand als versendet vermerkt, etwa weil
 *     er ihn gedruckt und mit der Post geschickt hat.
 *
 * Alles andere bleibt bearbeitbar: gespeichert, abgeschlossen, mit Nummer,
 * mit erzeugtem PDF. Die Nummer bleibt dabei dieselbe – sie ist vergeben und
 * wird nicht noch einmal verbraucht.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum auch «an mich selbst» als versendet zählt
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Raporto kennt drei Versandwege: an den Kunden, an die Firmenadresse und an
 * den angemeldeten Benutzer. Die beiden letzteren heissen im Programm «PDF
 * zum Weiterleiten» – ihr ganzer Zweck ist, dass der Betrieb das Blatt danach
 * selbst an den Kunden schickt.
 *
 * Ob er das getan hat, weiss Raporto nicht. Und genau deshalb zählt auch
 * dieser Weg: Ein PDF, das als E-Mail das Haus verlassen hat, kann beim
 * Kunden liegen. Es danach stillschweigend zu ändern, hiesse, zwei
 * verschiedene Blätter mit derselben Nummer in Umlauf zu bringen – der Fall,
 * den dieser Hotfix gerade verhindern soll. Im Zweifel gilt der Beleg als
 * draussen.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Storniert ist eine eigene Grenze
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Ein stornierter Beleg ist nicht «noch nicht draussen», sondern bewusst
 * zurückgezogen. Er bleibt lesbar und unveränderlich.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Der unklare Versand sperrt ebenfalls
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Es gibt einen dritten Zustand, und er ist der unangenehmste: Der Mailserver
 * hat nicht eindeutig geantwortet. Die Nachricht kann unterwegs sein, sie kann
 * beim Kunden liegen, sie kann im Nichts verschwunden sein. Niemand weiss es.
 *
 * Solange das so ist, bleibt der Beleg gesperrt. Er ist nicht «versendet» –
 * die Versandspalten bleiben leer, und im Zahlungsstand zählt er nicht als
 * erledigt –, aber er ist auch nicht mehr frei: Wer ihn jetzt ändert und ein
 * neues PDF erzeugt, riskiert genau das, was dieser Hotfix verhindern soll,
 * zwei verschiedene Blätter mit derselben Nummer.
 *
 * Aus diesem Zustand führt kein Zeitablauf heraus, sondern nur ein Mensch:
 * Entweder die Mail ist angekommen – dann wird der Beleg von Hand als
 * versendet vermerkt –, oder sie ist es nicht – dann wird der Versuch
 * ausdrücklich als gescheitert geklärt und der Beleg wieder frei. Beides ist
 * eine Entscheidung mit Namen und Zeitstempel, kein stilles Aufräumen.
 *
 * Dasselbe gilt für `wird_gesendet`: Da läuft gerade einer.
 */

export interface Versandmerkmale {
  status: string
  /** Zeitpunkt des erfolgreichen Versands an den Kunden, oder null. */
  gesendetAnKundeAm?: string | Date | null
  /** Zeitpunkt des Versands an die Firmenadresse, oder null. */
  gesendetAnFirmaAm?: string | Date | null
  /** Zeitpunkt des Versands an den angemeldeten Benutzer, oder null. */
  gesendetAnBenutzerAm?: string | Date | null
  /** Zeitpunkt des manuellen Vermerks «ist versendet», oder null. */
  versendetVermerktAm?: string | Date | null
}

function gesetzt(w: string | Date | null | undefined): boolean {
  return w !== null && w !== undefined && w !== ''
}

/** Hat der Beleg das Haus verlassen? */
export function istVersendet(d: Versandmerkmale): boolean {
  return (
    gesetzt(d.gesendetAnKundeAm) ||
    gesetzt(d.gesendetAnFirmaAm) ||
    gesetzt(d.gesendetAnBenutzerAm) ||
    gesetzt(d.versendetVermerktAm) ||
    /*
      Der Webhook des Mailanbieters bestätigt die Zustellung und setzt den
      Status. Er ist der dritte Weg, auf dem Raporto erfährt, dass der Beleg
      angekommen ist – und er kann eintreffen, bevor die Spalte gesetzt wurde.
    */
    d.status === 'gesendet'
  )
}

/**
 * Ist für diesen Beleg ein Versand unterwegs oder unklar geblieben?
 *
 * Bewusst getrennt von `istVersendet`: Der Beleg gilt NICHT als draussen – er
 * taucht in keiner Auswertung als versendet auf – und ist trotzdem gesperrt,
 * weil er es sein könnte.
 */
export function versandOffen(d: Versandmerkmale): boolean {
  return d.status === 'wird_gesendet' || d.status === 'versand_unklar'
}

export type Sperrgrund = 'versendet' | 'storniert' | 'versand_offen'

export interface Bearbeitungssperre {
  grund: Sperrgrund
  /** Was dem Benutzer gesagt wird. */
  text: string
}

/**
 * Darf dieser Beleg verändert werden?
 *
 * `null` heisst ja. Alles andere nennt den Grund – in Worten, die ein
 * Handwerker versteht, und mit dem Weg, der stattdessen offensteht.
 */
export function bearbeitungssperre(d: Versandmerkmale): Bearbeitungssperre | null {
  if (d.status === 'storniert') {
    return {
      grund: 'storniert',
      text:
        'Dieser Beleg ist storniert und lässt sich nicht mehr bearbeiten. ' +
        'Für eine Korrektur dupliziere ihn und stelle den neuen Beleg aus.'
    }
  }
  if (istVersendet(d)) {
    return {
      grund: 'versendet',
      text:
        'Dieser Beleg ist versendet und lässt sich nicht mehr ändern. ' +
        'Für eine Korrektur storniere ihn und stelle einen neuen Beleg aus – ' +
        'so bleibt nachvollziehbar, was der Kunde bekommen hat.'
    }
  }
  if (versandOffen(d)) {
    return {
      grund: 'versand_offen',
      text:
        d.status === 'wird_gesendet'
          ? 'Für diesen Beleg läuft gerade ein Versand. Bitte warte, bis das ' +
            'Ergebnis feststeht.'
          : 'Der letzte Versand dieses Belegs ist unklar geblieben – die Mail kann ' +
            'beim Kunden liegen. Darum bleibt der Beleg gesperrt. Kläre in der ' +
            'Versandhistorie, ob sie angekommen ist: Wenn ja, vermerke den Beleg ' +
            'als versendet. Wenn nein, gib ihn dort ausdrücklich wieder frei.'
    }
  }
  return null
}
